import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radar, Loader2, Eye, X, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusBadge, ConfidenceBar } from '@/components/common/StatusBadge';
import { Loader, EmptyState } from '@/components/common/Feedback';
import { DetectionDrawer } from '@/components/detections/DetectionDrawer';
import { ClassificationBadge } from '@/components/opportunities/OppBadges';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function DetectionInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [selected, setSelected] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = statusFilter === 'all' ? {} : { status: statusFilter };
    api.get('/detections', { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => b.confidence - a.confidence);
    return arr;
  }, [items]);

  const generate = async (det) => {
    setGeneratingId(det.id);
    try {
      const r = await api.post(`/detections/${det.id}/generate-co`);
      if (r.data.status === 'done' && r.data.co_id) {
        setGeneratingId(null);
        navigate(`/change-orders/${r.data.co_id}`);
        return;
      }
      toast('Drafting change order\u2026 this can take up to a minute.');
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/detections/${det.id}/generate-co/status`);
          if (s.data.status === 'done' && s.data.co_id) {
            clearInterval(poll); setGeneratingId(null);
            navigate(`/change-orders/${s.data.co_id}`);
          } else if (s.data.status === 'failed') {
            clearInterval(poll); setGeneratingId(null);
            toast.error(s.data.error || 'Generation failed');
          }
        } catch {
          clearInterval(poll); setGeneratingId(null);
          toast.error('Could not check generation status');
        }
      }, 3000);
    } catch (e) {
      setGeneratingId(null);
      toast.error(e?.response?.data?.detail || 'Generation failed');
    }
  };

  const dismiss = async (det) => {
    await api.post(`/detections/${det.id}/dismiss`);
    toast('Detection dismissed');
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Raw AI detections across all projects (founder debug view). Monetary impact is never guessed by AI.
        </p>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="detection-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="new">Awaiting review</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <Loader /> : sorted.length === 0 ? (
          <EmptyState icon={Radar} title="No detections here" description="When new project communications arrive, scope-change detections will appear in this inbox." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5">Detected Change</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Project / Trade</th>
                  <th className="text-left px-4 py-2.5">Confidence</th>
                  <th className="text-left px-4 py-2.5">Classification</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((d) => (
                  <tr key={d.id} className="row-hover cursor-pointer" onClick={() => setSelected(d)} data-testid={`detection-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{d.title}</div>
                      <div className="text-xs text-slate-500 line-clamp-1 max-w-md">{d.summary}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-slate-700">{d.project_name}</div>
                      <div className="text-xs text-slate-500">{d.trade}</div>
                    </td>
                    <td className="px-4 py-3"><ConfidenceBar value={d.confidence} /></td>
                    <td className="px-4 py-3"><ClassificationBadge classification={d.classification} /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {d.status === 'new' ? (
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" className="bg-money hover:bg-money-700 h-8"
                            disabled={generatingId === d.id} onClick={() => generate(d)} data-testid={`generate-co-${d.id}`}>
                            {generatingId === d.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                            Draft CO
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => dismiss(d)} data-testid={`dismiss-${d.id}`}>
                            <X className="h-4 w-4 text-slate-400" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" className="h-8" onClick={() => setSelected(d)}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <DetectionDrawer detection={selected} onClose={() => setSelected(null)}
        onGenerate={generate} generatingId={generatingId} />
    </div>
  );
}
